import type { BoxSx, FlagIconNameType } from "@modernatx-internal/component-library";
import {
  Box,
  Divider,
  FlagIcon,
  Icon,
  SidePanel,
  Target,
  Text
} from "@modernatx-internal/component-library";
import { ModernaAtlasLogo } from "@modernatx-internal/component-library/logo/ModernaAtlasLogo";
import React from "react";

import { useClickOutside } from "@/hooks/useClickOutside";

export interface AtlasNavBarLinkItem {
  label: string;
  href: string;
}

export interface AtlasNavBarLink {
  label: string;
  href?: string;
  subLinks?: AtlasNavBarLinkItem[];
}

export interface LanguageOption {
  country: string;
  flagIcon: FlagIconNameType;
  languages: React.ReactNode[];
}

export interface AtlasNavBarBlockProps {
  disclaimer?: React.ReactNode;
  logo: {
    href: string;
  };
  actionLink?: AtlasNavBarLinkItem;
  languageSelector?: {
    currentLocale: React.ReactNode;
    currentCountry: React.ReactNode;
    flagIcon: FlagIconNameType;
    title: React.ReactNode;
    otherLocationsTitle?: React.ReactNode;
    options: LanguageOption[];
  };
  navLinks?: AtlasNavBarLink[];
  activeHref?: string;
  search?: AtlasNavBarLinkItem;
}

const hoverUnderlineStyles: BoxSx = {
  position: "relative",
  "::after": {
    content: '""',
    position: "absolute",
    width: "100%",
    height: () => "3px",
    left: 0,
    bottom: 0,
    backgroundColor: "color-current-brand-600",
    opacity: 0,
    transition: "transition-color-medium"
  },
  ":hover::after": {
    opacity: 1
  }
};

const ActionLink = ({ link }: { link: AtlasNavBarLinkItem }) => (
  <Target
    href={link.href}
    target="_self"
    sx={{ textDecoration: "none", color: "color-current-brand-950" }}
  >
    <Text size="font-size-3" weight="bold">
      {link.label}
    </Text>
  </Target>
);

const LanguageSelector = ({
  currentLocale,
  flagIcon,
  onClick
}: {
  currentLocale: React.ReactNode;
  flagIcon: FlagIconNameType;
  onClick?: () => void;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }} onClick={onClick}>
    <FlagIcon flag={flagIcon} size="sm" />
    <Box sx={{ height: 6, display: "flex", alignItems: "center" }}>
      <Divider color="color-current-brand-950" direction="vertical" thickness="sm" />
    </Box>
    {currentLocale}
    <Icon icon="caret-down" size="xxs" color="color-current-brand-secondary-600" />
  </Box>
);

const SearchBox = ({ link }: { link: AtlasNavBarLinkItem }) => (
  <Target
    href={link.href}
    target="_self"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 3,
      cursor: "pointer",
      textDecoration: "none",
      ":hover": { textDecoration: "underline" }
    }}
  >
    <Icon icon="magnifying-glass" size="sm" color="color-current-brand-950" />
    <Text size="font-size-4" weight="bold" color="color-current-brand-950">
      {link.label}
    </Text>
  </Target>
);

const DisclaimerBar = ({ text }: { text: React.ReactNode }) => (
  <Box
    sx={{
      backgroundColor: "color-current-brand-950",
      px: [4, 6, 8],
      py: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      textWrap: "balance"
    }}
  >
    {text}
  </Box>
);

const MainNavLinks = ({
  links,
  activeLink,
  onLinkClick
}: {
  links: AtlasNavBarLink[];
  activeLink: AtlasNavBarLink | null;
  onLinkClick: (link: AtlasNavBarLink) => void;
}) => {
  const navLinkStyles: BoxSx = {
    py: 4,
    textDecoration: "none",
    fontSize: "font-size-4",
    color: "color-current-brand-950",
    fontWeight: "font-weight-medium",
    cursor: "pointer",
    ...hoverUnderlineStyles
  };

  return (
    <Box as="nav" sx={{ display: "flex", gap: 8, justifyContent: "center" }}>
      {links.map((link) => {
        const isActive = activeLink === link;
        const activeLinkStyles: BoxSx = {
          "::after": {
            ...hoverUnderlineStyles["::after"],
            backgroundColor: "color-current-brand-950",
            opacity: 1
          },
          ":hover::after": {
            opacity: 1,
            backgroundColor: "color-current-brand-950"
          }
        };

        return (
          <Target
            key={link.label}
            href={link.subLinks ? undefined : link.href}
            target="_self"
            sx={{
              ...navLinkStyles,
              ...(isActive ? activeLinkStyles : {})
            }}
            onClick={(e) => {
              if (link.subLinks) {
                e.preventDefault();
                onLinkClick(link);
              }
            }}
          >
            {link.label}
          </Target>
        );
      })}
    </Box>
  );
};

const GlobalNav = ({
  logo,
  actionLink,
  languageSelector,
  search,
  onLanguageSelectorClick,
  onMobileMenuClick
}: {
  logo: AtlasNavBarBlockProps["logo"];
  actionLink?: AtlasNavBarLinkItem;
  languageSelector?: AtlasNavBarBlockProps["languageSelector"];
  search?: AtlasNavBarLinkItem;
  onLanguageSelectorClick: () => void;
  onMobileMenuClick: () => void;
}) => (
  <Box
    sx={{
      backgroundColor: "white",
      px: [4, 8, 15],
      py: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      position: "relative",
      zIndex: "navbar"
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Target href={logo.href} target="_self">
        <ModernaAtlasLogo sx={{ height: 6, width: 41 }} />
      </Target>
    </Box>

    <Box sx={{ display: ["none", "none", "flex"], alignItems: "center", gap: 8 }}>
      {actionLink && <ActionLink link={actionLink} />}
      {languageSelector && (
        <LanguageSelector
          currentLocale={languageSelector.currentLocale}
          flagIcon={languageSelector.flagIcon}
          onClick={onLanguageSelectorClick}
        />
      )}
    </Box>

    <Box sx={{ display: ["flex", "flex", "none"], alignItems: "center", gap: 4 }}>
      {languageSelector && (
        <Box
          sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 2 }}
          onClick={onLanguageSelectorClick}
        >
          <FlagIcon flag={languageSelector.flagIcon} size="sm" />
          <Icon icon="caret-down" size="xxs" color="color-current-brand-secondary-600" />
        </Box>
      )}
      {search && (
        <Target href={search.href} target="_self" sx={{ display: "flex", alignItems: "center" }}>
          <Icon icon="magnifying-glass" size="sm" color="color-current-brand-950" />
        </Target>
      )}
      <Box
        sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        onClick={onMobileMenuClick}
      >
        <Icon icon="list" size="sm" color="color-current-brand-950" />
      </Box>
    </Box>
  </Box>
);

const MainNav = ({
  navLinks,
  activeLink,
  search,
  onLinkClick
}: {
  navLinks?: AtlasNavBarLink[];
  activeLink: AtlasNavBarLink | null;
  search?: AtlasNavBarLinkItem;
  onLinkClick: (link: AtlasNavBarLink) => void;
}) => (
  <Box
    sx={{
      backgroundColor: "color-current-brand-200",
      px: 15,
      display: "flex",
      alignItems: "center"
    }}
  >
    <Box sx={{ flex: 1 }} />
    {navLinks && (
      <MainNavLinks links={navLinks} activeLink={activeLink} onLinkClick={onLinkClick} />
    )}
    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
      {search && <SearchBox link={search} />}
    </Box>
  </Box>
);

const SubNav = ({ links }: { links: { label: React.ReactNode; href: string }[] }) => (
  <Box
    sx={{
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: "color-current-brand-50",
      py: 5,
      display: "flex",
      justifyContent: "center",
      borderTop: "1px solid",
      borderColor: "color-current-brand-100",
      gap: 8
    }}
  >
    {links.map((link) => (
      <Target
        key={link.href}
        href={link.href}
        target="_self"
        sx={{
          textDecoration: "none",
          color: "color-current-brand-950",
          fontSize: "font-size-4",
          fontWeight: "font-weight-bold",
          ":hover": { textDecoration: "underline" }
        }}
      >
        {link.label}
      </Target>
    ))}
  </Box>
);

const LanguageSelectorPanel = ({
  isOpen,
  onClose,
  currentCountry,
  currentLocale,
  currentFlagIcon,
  title,
  otherLocationsTitle,
  options
}: {
  isOpen: boolean;
  onClose: () => void;
  currentCountry: React.ReactNode;
  currentLocale: React.ReactNode;
  currentFlagIcon: FlagIconNameType;
  title: React.ReactNode;
  otherLocationsTitle?: React.ReactNode;
  options: LanguageOption[];
}) => {
  return (
    <SidePanel visible={isOpen} onDismiss={onClose}>
      <Box sx={{ p: [5, 6, 8] }}>
        <Box as="h2" sx={{ mb: 6 }}>
          {title}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 6 }}>
          <FlagIcon flag={currentFlagIcon} size="sm" />
          {currentCountry}
          {currentLocale}
        </Box>

        <Divider color="stroke-neutral-secondary" thickness="sm" sx={{ my: 6 }} />

        <Box as="h2" sx={{ mb: 6 }}>
          {otherLocationsTitle}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {options.map((location, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}
            >
              <FlagIcon flag={location.flagIcon} size="sm" />
              <Text size="font-size-5" weight="bold" color="color-current-brand-950">
                {location.country}
              </Text>
              {location.languages.map((lang, langIndex) => (
                <React.Fragment key={langIndex}>{lang}</React.Fragment>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </SidePanel>
  );
};

const MobileMenuPanel = ({
  isOpen,
  onClose,
  actionLink,
  navLinks
}: {
  isOpen: boolean;
  onClose: () => void;
  actionLink?: AtlasNavBarLinkItem;
  navLinks?: AtlasNavBarLink[];
}) => {
  const [expandedLink, setExpandedLink] = React.useState<AtlasNavBarLink | null>(null);

  const handleLinkClick = (link: AtlasNavBarLink) => {
    if (link.subLinks) {
      setExpandedLink(expandedLink === link ? null : link);
    }
  };

  return (
    <SidePanel visible={isOpen} onDismiss={onClose}>
      <Box sx={{ p: 6 }}>
        {actionLink && (
          <Box
            sx={{ mb: 6, pb: 6, borderBottom: "1px solid", borderColor: "color-current-brand-200" }}
          >
            <Target
              href={actionLink.href}
              target="_self"
              sx={{
                textDecoration: "none",
                color: "color-system-blue-950",
                fontSize: "font-size-5",
                fontWeight: "font-weight-bold"
              }}
            >
              {actionLink.label}
            </Target>
          </Box>
        )}

        {navLinks && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {navLinks.map((link) => {
              const isExpanded = expandedLink === link;
              const hasSubLinks = link.subLinks && link.subLinks.length > 0;

              return (
                <Box key={link.label}>
                  <Target
                    href={hasSubLinks ? undefined : link.href}
                    target="_self"
                    sx={{
                      textDecoration: "none",
                      color: isExpanded
                        ? "color-current-brand-secondary-700"
                        : "color-system-blue-950",
                      fontSize: "font-size-7",
                      fontWeight: "font-weight-medium",
                      display: "block",
                      cursor: "pointer"
                    }}
                    onClick={(e) => {
                      if (hasSubLinks) {
                        e.preventDefault();
                        handleLinkClick(link);
                      }
                    }}
                  >
                    {link.label}
                  </Target>

                  {hasSubLinks && isExpanded && (
                    <Box sx={{ mt: 3, ml: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      {link.subLinks?.map((subLink) => (
                        <Target
                          key={subLink.href}
                          href={subLink.href}
                          target="_self"
                          sx={{
                            textDecoration: "none",
                            color: "color-system-blue-950",
                            fontSize: "font-size-5",
                            fontWeight: "font-weight-bold",
                            ":hover": { textDecoration: "underline" }
                          }}
                        >
                          {subLink.label}
                        </Target>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </SidePanel>
  );
};

export const AtlasNavBar: React.FC<AtlasNavBarBlockProps> = ({
  disclaimer,
  logo,
  actionLink,
  languageSelector,
  navLinks,
  search
}) => {
  const [activeSubNav, setActiveSubNav] = React.useState<AtlasNavBarLink | null>(null);
  const [isLanguagePanelOpen, setIsLanguagePanelOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const desktopNavRef = React.useRef<HTMLDivElement>(null);

  const closeSubNav = React.useCallback(() => setActiveSubNav(null), []);
  useClickOutside(desktopNavRef, closeSubNav);

  const handleSubLinkClick = (link: AtlasNavBarLink) => {
    setActiveSubNav(activeSubNav === link ? null : link);
  };

  const hasOverlay = activeSubNav || isLanguagePanelOpen || isMobileMenuOpen;

  return (
    <Box
      as="header"
      sx={{
        width: "100%",
        position: "relative"
      }}
    >
      {hasOverlay && (
        <Box
          style={{
            backgroundColor: "colors-system-gray-950",
            zIndex: 1050 // Below the navbar
          }}
          sx={{
            position: "fixed",
            inset: 0,
            backdropFilter: "blur(8px)"
          }}
        />
      )}

      {disclaimer && <DisclaimerBar text={disclaimer} />}

      <GlobalNav
        logo={logo}
        actionLink={actionLink}
        languageSelector={languageSelector}
        search={search}
        onLanguageSelectorClick={() => setIsLanguagePanelOpen(true)}
        onMobileMenuClick={() => setIsMobileMenuOpen(true)}
      />

      <Box
        ref={desktopNavRef}
        sx={{
          position: "relative",
          zIndex: "dropdown",
          display: ["none", "none", "block"]
        }}
      >
        <MainNav
          navLinks={navLinks}
          activeLink={activeSubNav}
          search={search}
          onLinkClick={handleSubLinkClick}
        />

        {activeSubNav && activeSubNav.subLinks && <SubNav links={activeSubNav.subLinks} />}
      </Box>

      {languageSelector && (
        <LanguageSelectorPanel
          isOpen={isLanguagePanelOpen}
          currentCountry={languageSelector.currentCountry}
          currentLocale={languageSelector.currentLocale}
          currentFlagIcon={languageSelector.flagIcon}
          title={languageSelector.title}
          otherLocationsTitle={languageSelector.otherLocationsTitle}
          options={languageSelector.options}
          onClose={() => setIsLanguagePanelOpen(false)}
        />
      )}

      <MobileMenuPanel
        isOpen={isMobileMenuOpen}
        actionLink={actionLink}
        navLinks={navLinks}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </Box>
  );
};
